import type { HTMLTemplateResult } from "lit";
import { html, LitElement, nothing, unsafeCSS } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ref } from "lit/directives/ref.js";
import EventController from "@/common/controllers/event";
import SlotController from "@/common/controllers/slot";
import EkoChoiceElement from "@/utilities/choice-element";
import { cn } from "@/utilities/css";
import style from "./checkbox.css?inline";
import "@/components/field/hint";
import "@/components/label";

@customElement("eko-checkbox")
export class Checkbox extends EkoChoiceElement {
  static shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true };
  static styles = [...EkoChoiceElement.styles, unsafeCSS(style)];

  protected validationMessage: string = "Please check this box if you want to proceed.";

  private events = new EventController(this);

  // Checked behaviour is handled manually as click events do not bubble
  // from slotted inputs, or slotted labels due to shadow DOM vs light DOM boundaries.
  private handleClick = (event: Event) => {
    event.preventDefault();

    if (this.disabled) return;
    if (!this.inputElement) return;

    this.checked = !this.checked;
    this.inputElement.focus();
    this.removeAttribute("focus-visible"); // remove focus styles on click
    this.emitChange();
  };

  private inputSlot = new SlotController(this, "input");

  private onFocusIn = (e: Event) => {
    const target = e.composedPath()[0] as Element;
    this.toggleAttribute("focus-visible", target.matches(":focus-visible"));
  };

  private onFocusOut = () => {
    this.removeAttribute("focus-visible");
  };

  @property({ reflect: true, type: Boolean }) admin: boolean = false;
  @property() class: string = "";
  @property({ reflect: true }) size: "md" | "lg" = "md";
  @property({ reflect: true }) theme: "default" | "danger" = "default";

  private handleInputSlotChange() {
    if (!this.slotted) return;

    const input = this.querySelector(
      'input[type="checkbox"][slot="input"]'
    ) as HTMLInputElement | null;

    if (!input) return;
    this.slottedInput = input;

    input.classList.add("eko-checkbox__input");

    input.onchange = (e: Event) => this.handleChange(e);
    input.onfocus = () => this.handleFocus();
    input.onblur = () => this.handleBlur();

    const changedProperties = new Map<string, any>();
    changedProperties.set("checked", input.checked);
    changedProperties.set("disabled", input.disabled);
    changedProperties.set("required", input.required);
    this.updated(changedProperties);
  }

  connectedCallback() {
    super.connectedCallback();
    this.events.listen(this, "click", this.handleClick);
    this.events.listen(this, "focusin", this.onFocusIn);
    this.events.listen(this, "focusout", this.onFocusOut);
  }

  firstUpdated() {
    this.handleInputSlotChange();
  }

  render() {
    return html`<div class=${cn("eko-checkbox", this.class)}>
      <label class="eko-checkbox__label">
        <span class="eko-checkbox__wrapper">
          ${this.renderInput()}
          <span class="eko-checkbox__icon">${this.renderIcon()}</span>
        </span>
        <slot name="label"></slot>
      </label>
      ${this.hint ? html`<eko-hint>${this.hint}</eko-hint>` : nothing}
    </div>`;
  }

  renderIcon(): HTMLTemplateResult {
    return html`<eko-icon name="check" size="sm"></eko-icon>`;
  }

  renderInput(): HTMLTemplateResult {
    if (this.slotted) {
      return html`<slot
        name=${this.inputSlot.name}
        @slotchange=${this.handleInputSlotChange}
        ${ref(this.inputRef)}
        class="eko-checkbox__input"></slot>`;
    } else {
      return html`<input
        ${ref(this.inputRef)}
        type="checkbox"
        ?checked=${this.checked}
        @focus=${this.handleFocus}
        @blur=${this.handleBlur}
        name=${this.name}
        ?required=${this.required}
        value=${this.value}
        ?disabled=${this.disabled}
        ?admin=${this.admin}
        class="eko-checkbox__input" />`;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-checkbox": Checkbox;
  }
}
